import { type ReactNode, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getPassage, recordPlay, recordStart, SubscriptionRequiredError } from "../api";
import { Badge } from "../components/Badge";
import { Paywall } from "../components/Paywall";
import { RsvpPlayer } from "../components/RsvpPlayer";
import { ErrorBox, Loading, useResource } from "../ui";

export function Play() {
  const { id = "" } = useParams();
  return <PlayPassage key={id} id={id} />;
}

function Frame({ children }: { children: ReactNode }) {
  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <Link to="/app" className="text-[12px] font-bold text-ink-2 hover:text-shu">
        × ホームへ
      </Link>
      <div className="mt-4">{children}</div>
    </div>
  );
}

const SKIP_MS = 1500;
const POLL_MS = 3000;

function PlayPassage({ id }: { id: string }) {
  const navigate = useNavigate();
  const go = (to: string) => navigate(`/app/p/${to}`, { replace: true });
  const [version, setVersion] = useState(0);
  const { data, loading, error } = useResource(
    () =>
      getPassage(id).catch((e) => {
        if (e instanceof SubscriptionRequiredError) return e;
        throw e;
      }),
    [id, version],
  );
  const notReady =
    data && !(data instanceof SubscriptionRequiredError) && (data.audioStatus !== "ready" || !data.audioUrl || !data.segments.length);
  const pending = notReady && (data.audioStatus === "queued" || data.audioStatus === "processing");
  const skipTo = notReady && !pending && !data.owned ? data.nextId : null;

  useEffect(() => {
    if (!pending || loading) return;
    const t = setTimeout(() => setVersion((v) => v + 1), POLL_MS);
    return () => clearTimeout(t);
  }, [pending, loading, version]);

  useEffect(() => {
    if (!skipTo) return;
    const t = setTimeout(() => go(skipTo), SKIP_MS);
    return () => clearTimeout(t);
  }, [skipTo]);

  if (loading && !pending) return <Loading />;
  if (error || !data)
    return (
      <Frame>
        <ErrorBox message={error ?? "パッセージが見つかりません。"} />
      </Frame>
    );
  if (data instanceof SubscriptionRequiredError)
    return (
      <Frame>
        <Paywall title={data.collection?.title} />
      </Frame>
    );
  const { audioUrl } = data;
  if (notReady || !audioUrl)
    return (
      <Frame>
        <div className="border-2 border-ink bg-sheet px-4 py-4 text-[13px] font-bold">
          {pending ? (
            <p>音声を準備しています。準備ができたら自動で始まります…</p>
          ) : (
            <>
              <Badge tone="admin">{data.audioStatus}</Badge>
              <p className="mt-2">音声を用意できませんでした。{skipTo && "次のパッセージへ進みます。"}</p>
            </>
          )}
        </div>
      </Frame>
    );

  return (
    <RsvpPlayer
      key={version}
      passage={{ ...data, audioUrl }}
      onEdited={() => setVersion((v) => v + 1)}
      onPass={(play) => recordPlay(id, play).catch(() => {})}
      onStart={(mode) => recordStart(id, mode).catch(() => {})}
      onGo={go}
      onRestart={() => navigate(`/app/c/${data.collection.slug}?from=first`, { replace: true })}
      onClose={() => navigate("/app")}
    />
  );
}
